<div class="border-b border-slate-200 px-6 py-3 space-y-2.5">

  <!-- Row 1: search + mobile toggle + sort + count -->
  <div class="flex flex-wrap items-center gap-2">

    <!-- Search -->
    <div class="relative min-w-[160px] flex-1 max-w-[280px]">
      <span class="pointer-events-none absolute inset-y-0 left-0 flex w-7 items-center justify-center">
        <lucide-icon name="search" [size]="13" [strokeWidth]="2" class="text-slate-400"></lucide-icon>
      </span>
      <input
        type="search"
        [placeholder]="placeholder"
        [ngModel]="searchQuery"
        (ngModelChange)="onQueryChange($event)"
        class="w-full rounded-md border border-slate-200 bg-white py-1.5 pl-7 pr-3 text-sm text-slate-900 placeholder-slate-400 focus:border-violet-400 focus:outline-none focus:ring-1 focus:ring-violet-300"
        [attr.aria-label]="'Search ' + entityName"
      />
    </div>

    <!-- Quota usage pill -->
    <span
      *ngIf="quotaUsage"
      class="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] transition-colors"
      [class.border-red-200]="quotaUsage.remaining === 0"
      [class.bg-red-50]="quotaUsage.remaining === 0"
      [class.text-red-700]="quotaUsage.remaining === 0"
      [class.border-amber-200]="quotaUsage.remaining > 0 && quotaUsage.percentUsed >= 80"
      [class.bg-amber-50]="quotaUsage.remaining > 0 && quotaUsage.percentUsed >= 80"
      [class.text-amber-700]="quotaUsage.remaining > 0 && quotaUsage.percentUsed >= 80"
      [class.border-slate-200]="quotaUsage.percentUsed < 80"
      [class.text-slate-500]="quotaUsage.percentUsed < 80"
    >
      <lucide-icon name="activity" [size]="10" [strokeWidth]="2" class="shrink-0"></lucide-icon>
      <span>Monitored</span>
      <span class="font-semibold tabular-nums">{{ quotaUsage.currentCount }} / {{ quotaUsage.limit }}</span>
      <ng-container *ngIf="quotaUsage.remaining === 0">
        <span aria-hidden="true">·</span>
        <span class="font-semibold">Limit reached</span>
      </ng-container>
      <ng-container *ngIf="quotaUsage.remaining > 0 && quotaUsage.percentUsed >= 80">
        <span aria-hidden="true">·</span>
        <span>{{ quotaUsage.remaining }} remaining</span>
      </ng-container>
    </span>

    <!-- Mobile filter toggle (only shown when isMobile) -->
    <button
      *ngIf="isMobile"
      (click)="mobileOpen = !mobileOpen"
      class="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-violet-300"
      [class.border-violet-500]="hasActiveFacets || mobileOpen"
      [class.text-violet-700]="hasActiveFacets || mobileOpen"
      [class.bg-violet-50]="hasActiveFacets || mobileOpen"
      [class.border-slate-200]="!hasActiveFacets && !mobileOpen"
      [class.text-slate-600]="!hasActiveFacets && !mobileOpen"
      [attr.aria-expanded]="mobileOpen"
      aria-controls="filter-facets-panel"
    >
      <lucide-icon name="filter" [size]="13" [strokeWidth]="2"></lucide-icon>
      Filters
      <span
        *ngIf="activeFacetCount > 0"
        class="rounded-full bg-violet-600 px-1.5 py-px text-[10px] font-bold leading-none text-white"
      >{{ activeFacetCount }}</span>
    </button>

    <!-- Sort -->
    <select
      [ngModel]="currentSort"
      (ngModelChange)="onSortChange($event)"
      class="ml-auto rounded-md border border-slate-200 bg-white py-1.5 pl-2.5 pr-7 text-xs text-slate-600 focus:border-violet-400 focus:outline-none focus:ring-1 focus:ring-violet-300"
      aria-label="Sort order"
    >
      <option *ngFor="let opt of sortOptions" [value]="opt.value">{{ opt.label }}</option>
    </select>

    <!-- Result count -->
    <p class="whitespace-nowrap text-xs text-slate-400">
      <span class="font-medium text-slate-600">{{ resultCount }}</span> of {{ totalCount }}
    </p>

  </div>

  <!-- Facet dropdowns -->
  <div
    id="filter-facets-panel"
    *ngIf="facetsVisible && facets.length > 0"
    class="flex flex-wrap items-center gap-2"
  >
    <div *ngFor="let facet of facets">
      <button
        #dropTrigger
        (click)="toggleDropdown(facet.key, dropTrigger)"
        class="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-violet-300"
        [class.border-violet-500]="isSelectedAny(facet.key) || openDropdown === facet.key"
        [class.bg-violet-50]="isSelectedAny(facet.key) || openDropdown === facet.key"
        [class.text-violet-700]="isSelectedAny(facet.key) || openDropdown === facet.key"
        [class.border-slate-200]="!isSelectedAny(facet.key) && openDropdown !== facet.key"
        [class.bg-white]="!isSelectedAny(facet.key) && openDropdown !== facet.key"
        [class.text-slate-600]="!isSelectedAny(facet.key) && openDropdown !== facet.key"
        [attr.aria-expanded]="openDropdown === facet.key"
        [attr.aria-label]="facet.label + ' filter'"
      >
        {{ facet.label }}
        <span
          *ngIf="selectedCount(facet.key) > 0"
          class="rounded-full bg-violet-600 px-1.5 py-px text-[10px] font-bold leading-none text-white"
        >{{ selectedCount(facet.key) }}</span>
        <lucide-icon
          name="chevron-down"
          [size]="11"
          [strokeWidth]="2"
          class="transition-transform"
          [class.rotate-180]="openDropdown === facet.key"
        ></lucide-icon>
      </button>

      <!-- Dropdown panel — fixed to escape overflow:hidden ancestors -->
      <div
        *ngIf="openDropdown === facet.key"
        (click)="$event.stopPropagation()"
        [style.top.px]="dropdownPosition.top"
        [style.left.px]="dropdownPosition.left"
        class="fixed z-50 min-w-[160px] rounded-md border border-slate-200 bg-white py-1 shadow-md"
        role="listbox"
        [attr.aria-label]="facet.label + ' options'"
        aria-multiselectable="true"
      >
        <button
          *ngFor="let opt of facet.options"
          (click)="toggleFacet(facet.key, opt.value)"
          class="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-50 focus:bg-slate-50 focus:outline-none"
          role="option"
          [attr.aria-selected]="isSelected(facet.key, opt.value)"
        >
          <span
            class="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border transition-colors"
            [class.border-violet-600]="isSelected(facet.key, opt.value)"
            [class.bg-violet-600]="isSelected(facet.key, opt.value)"
            [class.border-slate-300]="!isSelected(facet.key, opt.value)"
          >
            <lucide-icon
              *ngIf="isSelected(facet.key, opt.value)"
              name="check"
              [size]="9"
              [strokeWidth]="3"
              class="text-white"
            ></lucide-icon>
          </span>
          {{ opt.label }}
        </button>
      </div>
    </div>
  </div>

  <!-- Active filter pills + clear all -->
  <div *ngIf="activeFilterPills.length > 0" class="flex flex-wrap items-center gap-1.5">
    <button
      *ngFor="let pill of activeFilterPills"
      (click)="removePill(pill)"
      class="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700 hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-300"
      [attr.aria-label]="'Remove filter: ' + pill.label"
    >
      {{ pill.label }}
      <lucide-icon name="x" [size]="10" [strokeWidth]="2.5"></lucide-icon>
    </button>
    <button
      (click)="clearAll()"
      class="rounded text-xs text-slate-400 underline hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-violet-300"
    >
      Clear all
    </button>
  </div>

</div>
